import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { adminApi, ClientverseCoreBrowse, ClientverseCoreBrowseObject } from '../api/admin';

// Admin-only browse + edit of the ClientVerse Core schema. Same two-pane
// layout and save-on-blur UX as AdminStagingCanonicalBrowser, but
// deliberately NOT scoped to a single version — sf_objects/sf_fields are
// singleton rows (one per real object/field, already stamped with
// first_version_id/last_version_id; see 0030_clientverse_versions.sql), so
// every object/field is shown here regardless of when it was introduced or
// whether it's since been deprecated, with that range surfaced as a badge
// per row instead of being the thing you pick before browsing.
export default function AdminClientverseCoreBrowser() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [schema, setSchema] = useState<ClientverseCoreBrowse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await getToken();
        const body = await adminApi.getClientverseCoreBrowse(t);
        setSchema(body);
        if (body.objects.length > 0) setSelected((prev) => prev ?? body.objects[0].id);
      } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    })();
    /* eslint-disable-line */
  }, []);

  const filteredObjects = useMemo(() => {
    if (!schema) return [];
    const q = query.trim().toLowerCase();
    if (!q) return schema.objects;
    return schema.objects.filter((o) =>
      o.name.toLowerCase().includes(q) || (o.label ?? '').toLowerCase().includes(q),
    );
  }, [schema, query]);

  const selectedObj = useMemo(
    () => schema?.objects.find((o) => o.id === selected) ?? null,
    [schema, selected],
  );

  const mutateSchema = (fn: (draft: ClientverseCoreBrowse) => ClientverseCoreBrowse) => {
    setSchema((cur) => (cur ? fn(cur) : cur));
  };

  const saveObjectDesc = async (objectId: string, description: string | null) => {
    try {
      const t = await getToken();
      await adminApi.updateClientverseCoreObjectDescription(t, objectId, description);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => (o.id === objectId ? { ...o, description } : o)),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const saveFieldDesc = async (objectId: string, fieldId: string, description: string | null) => {
    try {
      const t = await getToken();
      await adminApi.updateClientverseCoreFieldDescription(t, fieldId, description);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => {
          if (o.id !== objectId) return o;
          return { ...o, fields: o.fields.map((f) => (f.id === fieldId ? { ...f, description } : f)) };
        }),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const saveFieldSample = async (objectId: string, fieldId: string, sample_data: string | null) => {
    try {
      const t = await getToken();
      await adminApi.updateClientverseCoreFieldSampleData(t, fieldId, sample_data);
      mutateSchema((s) => ({
        ...s,
        objects: s.objects.map((o) => {
          if (o.id !== objectId) return o;
          return { ...o, fields: o.fields.map((f) => (f.id === fieldId ? { ...f, sample_data } : f)) };
        }),
      }));
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const documented = useMemo(() => {
    if (!schema) return { objects: 0, fields: 0, totalFields: 0 };
    let objs = 0, docFields = 0, totalFields = 0;
    for (const o of schema.objects) {
      if ((o.description ?? '').trim()) objs++;
      for (const f of o.fields) {
        totalFields++;
        if ((f.description ?? '').trim()) docFields++;
      }
    }
    return { objects: objs, fields: docFields, totalFields };
  }, [schema]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Link to="/admin/modules" style={breadcrumb}>Modules</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <Link to="/admin/modules/data-mapping" style={breadcrumb}>Data Mapping</Link>
        <span style={{ color: 'var(--mi-muted)' }}>›</span>
        <h1 style={{ color: 'var(--mi-navy)', margin: 0 }}>ClientVerse Core</h1>
        <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>
      </div>
      <p style={{ color: 'var(--mi-muted)', marginTop: 4, fontSize: 13 }}>
        Every object and field ever synced from Core, across all versions — including deprecated ones, marked as such. Add descriptions on any of them; descriptions save on blur and aren't tied to a particular version, since the object or field itself isn't either.
      </p>

      {error && <div style={errBox}>{error}</div>}
      {!schema && !error && <div style={{ color: 'var(--mi-muted)', fontSize: 13 }}>Loading…</div>}

      {schema && (
        <>
          <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginBottom: 12 }}>
            {schema.objects.length} objects · {documented.totalFields} fields ·
            {' '}<strong>{documented.objects}</strong> objects and{' '}
            <strong>{documented.fields}</strong> fields documented
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 12, alignItems: 'start' }}>
            {/* LEFT: object picker */}
            <div className="card" style={{ height: 720, display: 'flex', flexDirection: 'column', padding: 12 }}>
              <input
                placeholder="Filter objects…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={input}
              />
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: 8 }}>
                {filteredObjects.length === 0 ? (
                  <div style={{ color: 'var(--mi-muted)', fontSize: 13, padding: 6 }}>No matches.</div>
                ) : (
                  filteredObjects.map((o) => {
                    const hasDesc = !!(o.description ?? '').trim();
                    const fieldTotal = o.fields.length;
                    const fieldDoc = o.fields.filter((f) => (f.description ?? '').trim()).length;
                    const isSel = selected === o.id;
                    const deprecated = o.archived_at !== null;
                    return (
                      <button
                        key={o.id}
                        onClick={() => setSelected(o.id)}
                        style={{
                          ...listItem,
                          background: isSel ? '#eef0f8' : '#fff',
                          borderColor: isSel ? 'var(--mi-purple)' : 'var(--mi-border)',
                          opacity: deprecated ? 0.7 : 1,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace', color: 'var(--mi-navy)' }}>
                          {o.name}
                          {deprecated && <span style={{ ...deprecatedTag, marginLeft: 6 }}>deprecated</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--mi-muted)', marginTop: 2 }}>
                          {hasDesc && <span title="Object has description" style={{ marginRight: 6, color: 'var(--mi-green)' }}>●</span>}
                          {fieldDoc}/{fieldTotal} fields documented
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--mi-muted)', marginTop: 2 }}>{versionRangeLabel(o)}</div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* RIGHT: object detail + fields */}
            <div style={{ minWidth: 0 }}>
              {!selectedObj ? (
                <div className="card" style={{ color: 'var(--mi-muted)' }}>Select an object.</div>
              ) : (
                <ObjectEditor
                  key={selectedObj.id}
                  obj={selectedObj}
                  onSaveObjectDesc={(desc) => saveObjectDesc(selectedObj.id, desc)}
                  onSaveFieldDesc={(fieldId, desc) => saveFieldDesc(selectedObj.id, fieldId, desc)}
                  onSaveFieldSample={(fieldId, sample) => saveFieldSample(selectedObj.id, fieldId, sample)}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ObjectEditor({ obj, onSaveObjectDesc, onSaveFieldDesc, onSaveFieldSample }: {
  obj: ClientverseCoreBrowseObject;
  onSaveObjectDesc: (description: string | null) => Promise<void>;
  onSaveFieldDesc: (fieldId: string, description: string | null) => Promise<void>;
  onSaveFieldSample: (fieldId: string, sample_data: string | null) => Promise<void>;
}) {
  const [fieldQuery, setFieldQuery] = useState('');
  const filteredFields = useMemo(() => {
    const q = fieldQuery.trim().toLowerCase();
    if (!q) return obj.fields;
    return obj.fields.filter((f) =>
      f.name.toLowerCase().includes(q)
        || (f.label ?? '').toLowerCase().includes(q)
        || (f.description ?? '').toLowerCase().includes(q),
    );
  }, [obj.fields, fieldQuery]);

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: 'var(--mi-navy)' }}>{obj.name}</span>
          {obj.archived_at !== null && <span style={deprecatedTag}>deprecated</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--mi-muted)', marginTop: 2 }}>
          {obj.fields.length} fields · {versionRangeLabel(obj)}
        </div>
      </div>

      <label style={fieldLabel}>Object description</label>
      <AutoSaveTextarea
        value={obj.description ?? ''}
        placeholder="Describe what this ClientVerse Core object represents…"
        onSave={(v) => onSaveObjectDesc(v.trim() ? v : null)}
        rows={3}
      />

      <div style={{ marginTop: 20, marginBottom: 8, display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: 'var(--mi-navy)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Fields
        </div>
        <input
          placeholder="Filter fields…"
          value={fieldQuery}
          onChange={(e) => setFieldQuery(e.target.value)}
          style={{ ...input, maxWidth: 240 }}
        />
        <span style={{ fontSize: 12, color: 'var(--mi-muted)' }}>
          {filteredFields.length} of {obj.fields.length} shown
        </span>
      </div>

      <div style={{ maxHeight: 560, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filteredFields.map((f) => (
          <div key={f.id} style={{ ...fieldRow, opacity: f.archived_at !== null ? 0.7 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: 'var(--mi-navy)' }}>{f.name}</span>
              {f.data_type && <span style={typeChip}>{f.data_type}</span>}
              {f.is_required && <span style={requiredChip}>required</span>}
              {f.length !== null && f.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--mi-muted)' }}>len {f.length}</span>
              )}
              {f.archived_at !== null && <span style={deprecatedTag}>deprecated</span>}
              <span style={{ fontSize: 10, color: 'var(--mi-muted)', marginLeft: 'auto' }}>{versionRangeLabel(f)}</span>
            </div>
            <AutoSaveTextarea
              value={f.description ?? ''}
              placeholder="Describe this field…"
              onSave={(v) => onSaveFieldDesc(f.id, v.trim() ? v : null)}
              rows={2}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <span style={sampleLabel}>Sample</span>
              <AutoSaveTextarea
                value={f.sample_data ?? ''}
                placeholder="e.g. Acme Corporation / 2026-08-19 / 1250.00"
                onSave={(v) => onSaveFieldSample(f.id, v.trim() ? v : null)}
                singleLine
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// "Since vX" while still active; "vX (deprecated)" or "vX – vY (deprecated)"
// once archived, derived purely from first/last_version_string + archived_at
// — no separate lookup needed, the browse endpoint already resolves these.
function versionRangeLabel(row: { first_version_string: string | null; last_version_string: string | null; archived_at: string | null }): string {
  if (!row.first_version_string) return '';
  if (row.archived_at === null) return `Since v${row.first_version_string}`;
  if (row.last_version_string && row.last_version_string !== row.first_version_string) {
    return `v${row.first_version_string} – v${row.last_version_string} (deprecated)`;
  }
  return `v${row.first_version_string} (deprecated)`;
}

// Save-on-blur input — identical behavior to AdminStagingCanonicalBrowser's.
function AutoSaveTextarea({ value, placeholder, onSave, rows, singleLine }: {
  value: string;
  placeholder?: string;
  onSave: (v: string) => Promise<void>;
  rows?: number;
  singleLine?: boolean;
}) {
  const [text, setText] = useState(value);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const original = useRef(value);
  useEffect(() => { setText(value); original.current = value; }, [value]);

  const handleBlur = async () => {
    if (text === original.current) return;
    setStatus('saving');
    try {
      await onSave(text);
      original.current = text;
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 1200);
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={{ position: 'relative', marginTop: singleLine ? 0 : 4, flex: 1, minWidth: 0 }}>
      {singleLine ? (
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          style={largeInput}
        />
      ) : (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          rows={rows ?? 2}
          style={textarea}
        />
      )}
      {status !== 'idle' && (
        <span style={{
          position: 'absolute', right: 6, bottom: singleLine ? 8 : 4, fontSize: 11,
          color: status === 'error' ? '#a11414' : status === 'saved' ? 'var(--mi-green)' : 'var(--mi-muted)',
        }}>
          {status === 'saving' ? 'saving…' : status === 'saved' ? 'saved' : 'error'}
        </span>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
const breadcrumb: React.CSSProperties = { color: 'var(--mi-muted)', fontSize: 14, textDecoration: 'none' };
const backBtn: React.CSSProperties = { marginLeft: 'auto', background: 'transparent', border: '1px solid var(--mi-border)', padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: 'var(--mi-muted)' };
const errBox: React.CSSProperties = { background: '#fee', color: '#900', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 14 };
const input: React.CSSProperties = { width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--mi-border)', fontSize: 13, boxSizing: 'border-box' };
const listItem: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', border: '1px solid var(--mi-border)',
  borderRadius: 6, padding: '8px 10px', cursor: 'pointer', marginBottom: 6, color: 'var(--mi-text)',
};
const fieldLabel: React.CSSProperties = { fontSize: 12, color: 'var(--mi-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 };
const sampleLabel: React.CSSProperties = { fontSize: 10, color: 'var(--mi-muted)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, minWidth: 44 };
const fieldRow: React.CSSProperties = { border: '1px solid var(--mi-border)', borderRadius: 6, padding: '8px 10px', background: '#fff' };
const textarea: React.CSSProperties = {
  width: '100%', padding: '6px 30px 6px 8px', borderRadius: 6, border: '1px solid var(--mi-border)',
  fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.4,
};
const largeInput: React.CSSProperties = {
  width: '100%', padding: '10px 60px 10px 12px', borderRadius: 6,
  border: '1px solid var(--mi-border)',
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'monospace',
  background: '#fbfdff',
};
const typeChip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#eef2ff', color: '#3b4ea1', border: '1px solid #d6deff', fontFamily: 'monospace', whiteSpace: 'nowrap',
};
const requiredChip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 6px', fontSize: 11, borderRadius: 4,
  background: '#fde7e7', color: '#a11414', border: '1px solid #f3c0c0', fontWeight: 600, whiteSpace: 'nowrap',
};
const deprecatedTag: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.4, color: '#a11414',
  background: '#fdecec', border: '1px solid #f3c0c0', borderRadius: 999, padding: '2px 8px',
};
